/**
 * Staged Products filter model.
 *
 * The bar renders only the filters that are actually applied, so the schema
 * lives here (as data) rather than as a fixed row of controls in the page:
 * field definitions, the URL <-> state serialisation that makes a filtered view
 * shareable, and the small helpers the pills/menus need.
 *
 * Values are stable slugs, never display labels — copy changes must not
 * invalidate bookmarked URLs or saved views. Brand is the one exception: the
 * brand name *is* the key the queue is filtered on.
 */

export type StagedFilterKey =
  | 'brand'
  | 'listing'
  | 'scrape'
  | 'change'
  | 'sort'
  | 'issues'
  | 'upc'
  | 'dupe'
  | 'ai';

export type StagedFilterGroup = 'Catalog' | 'Scraping' | 'Data quality' | 'Table';

export interface StagedFilterOption {
  value: string;
  label: string;
}

export interface StagedFilterFieldDef {
  key: StagedFilterKey;
  label: string;
  group: StagedFilterGroup;
  /** Multi-select fields collect string[]; single-select fields hold one slug. */
  multi: boolean;
  /**
   * Brand-style selection: clicking the name selects only that value, ticking
   * the box adds it to a multi-value selection. Brands are usually queried one
   * at a time and occasionally several.
   */
  exclusiveDefault?: boolean;
  /**
   * Values may be prefixed with `-` to exclude rows that have that option
   * (Data issues: click once to require, click again to hide).
   */
  allowExclude?: boolean;
  /** Label used on the pill while a multi field has nothing selected. */
  allLabel?: string;
  searchPlaceholder?: string;
  /** Static options; brand's are supplied from the loaded queue at runtime. */
  options: StagedFilterOption[];
}

/** Approval-readiness gaps. Values match `StagedApprovalGapId` plus two derived ids. */
export const STAGED_ISSUE_OPTIONS: StagedFilterOption[] = [
  { value: 'any_gap', label: 'Not approval-ready (any issue)' },
  { value: 'upc', label: 'Missing UPC / EAN' },
  { value: 'photo', label: 'Missing product image' },
  { value: 'longDescription', label: 'Long description too short (≤3 sentences)' },
  { value: 'shortDescription', label: 'Missing short description' },
  { value: 'features', label: 'Too few features (need >3)' },
  { value: 'price', label: 'Missing price' },
  { value: 'specs', label: 'Missing specs' },
  { value: 'inTheBox', label: 'Missing in the box' },
  { value: 'weight', label: 'Missing weight' },
  { value: 'category', label: 'Missing category' },
  { value: 'productType', label: 'Missing product type' },
  { value: 'sku', label: 'Missing SKU' },
  { value: 'fujifilmCatalog', label: 'Fujifilm: SKU/MPN not numeric (min 8 digits)' },
  { value: 'identifierReview', label: 'Odd or invalid identifier' },
  { value: 'verificationError', label: 'Critical AI verification error' },
  { value: 'contentConsistency', label: 'Content names a different product' },
  { value: 'junk_specs', label: 'Storefront junk in specs' },
  { value: 'lens_as_camera', label: 'Lens listed as a camera' },
  { value: 'kit_as_lens', label: 'Camera kit listed as a lens' },
  { value: 'accessory_as_camera', label: 'Battery/charger listed as a camera' },
  { value: 'suspicious_price', label: 'Suspicious low price (÷100?)' },
];

const LISTING_OPTIONS: StagedFilterOption[] = [
  { value: 'all', label: 'Active + discontinued' },
  { value: 'active_only', label: 'Active only' },
  { value: 'discontinued_only', label: 'Discontinued only' },
];

const SCRAPE_KIND_OPTIONS: StagedFilterOption[] = [
  { value: 'all', label: 'All scrapes' },
  { value: 'new', label: 'New products only' },
  { value: 'updates', label: 'Re-scrapes / updates only' },
];

const CHANGE_TYPE_OPTIONS: StagedFilterOption[] = [
  { value: 'all', label: 'All change types' },
  { value: 'new', label: 'New' },
  { value: 'full_update', label: 'Full Update' },
  { value: 'price_update', label: 'Price Update' },
  { value: 'content_update', label: 'Content Update' },
  { value: 'spec_update', label: 'Spec Update' },
  { value: 'status_change', label: 'Status Change' },
];

const GAP_SORT_OPTIONS: StagedFilterOption[] = [
  { value: 'none', label: 'List order' },
  { value: 'new_first', label: 'New products first' },
  { value: 'most', label: 'Most gaps first' },
  { value: 'fewest', label: 'Fewest gaps first' },
];

/** Whether the row carries a barcode that satisfies approve/promote. */
const UPC_STATE_OPTIONS: StagedFilterOption[] = [
  { value: 'all', label: 'Any UPC state' },
  { value: 'has', label: 'Has UPC / EAN' },
  { value: 'missing', label: 'Missing UPC / EAN' },
];

/**
 * Promote-blocking collisions. Separate from UPC state because the two stack:
 * "has a barcode" and "hide the ones that collide" is a normal way to work the
 * approve queue, and one merged single-select could not express it.
 */
const DUPLICATE_UPC_OPTIONS: StagedFilterOption[] = [
  { value: 'all', label: 'Any collision state' },
  { value: 'duplicates_only', label: 'Duplicate UPC only' },
  { value: 'hide_duplicates', label: 'Hide duplicate UPC' },
];

/** Client-side AI score buckets — thresholds match `aiScoreTone`. */
const AI_SCORE_OPTIONS: StagedFilterOption[] = [
  { value: 'all', label: 'All AI scores' },
  { value: 'high', label: 'High (≥80)' },
  { value: 'medium', label: 'Medium (60–79)' },
  { value: 'low', label: 'Low (<60)' },
  { value: 'below_80', label: 'Below 80' },
  { value: 'none', label: 'No AI score' },
];

export const STAGED_FILTER_FIELDS: StagedFilterFieldDef[] = [
  {
    key: 'brand',
    label: 'Brand',
    group: 'Catalog',
    multi: true,
    exclusiveDefault: true,
    allLabel: 'All brands',
    searchPlaceholder: 'Search brands',
    options: [],
  },
  { key: 'listing', label: 'Listing status', group: 'Catalog', multi: false, options: LISTING_OPTIONS },
  { key: 'scrape', label: 'Scrape kind', group: 'Scraping', multi: false, options: SCRAPE_KIND_OPTIONS },
  { key: 'change', label: 'Change type', group: 'Scraping', multi: false, options: CHANGE_TYPE_OPTIONS },
  { key: 'sort', label: 'Gap sort', group: 'Table', multi: false, options: GAP_SORT_OPTIONS },
  {
    key: 'issues',
    label: 'Data issues',
    group: 'Data quality',
    multi: true,
    allLabel: 'All products',
    searchPlaceholder: 'Search issues',
    allowExclude: true,
    options: STAGED_ISSUE_OPTIONS,
  },
  { key: 'upc', label: 'UPC state', group: 'Data quality', multi: false, options: UPC_STATE_OPTIONS },
  { key: 'dupe', label: 'Duplicate UPC', group: 'Data quality', multi: false, options: DUPLICATE_UPC_OPTIONS },
  { key: 'ai', label: 'AI score', group: 'Data quality', multi: false, options: AI_SCORE_OPTIONS },
];

export const STAGED_FILTER_KEYS: StagedFilterKey[] = STAGED_FILTER_FIELDS.map((f) => f.key);

const FIELD_BY_KEY: Record<string, StagedFilterFieldDef> = STAGED_FILTER_FIELDS.reduce(
  (acc, f) => {
    acc[f.key] = f;
    return acc;
  },
  {} as Record<string, StagedFilterFieldDef>,
);

export function stagedFilterField(key: StagedFilterKey): StagedFilterFieldDef {
  return FIELD_BY_KEY[key];
}

export function isStagedFilterKey(key: string): key is StagedFilterKey {
  return Object.prototype.hasOwnProperty.call(FIELD_BY_KEY, key);
}

/** The "no filter" value for a field: [] for multi, the leading option for single. */
export function stagedFilterDefault(key: StagedFilterKey): string | string[] {
  const field = FIELD_BY_KEY[key];
  return field.multi ? [] : field.options[0].value;
}

export interface StagedFilterState {
  /** Free-text search across name / SKU / MPN / brand. */
  q: string;
  brand: string[];
  issues: string[];
  listing: string;
  scrape: string;
  change: string;
  upc: string;
  dupe: string;
  ai: string;
  sort: string;
  /** Pill order — the order filters were added, so a shared link rebuilds the same bar. */
  order: StagedFilterKey[];
}

export const EMPTY_STAGED_FILTER_STATE: StagedFilterState = {
  q: '',
  brand: [],
  issues: [],
  listing: 'all',
  scrape: 'all',
  change: 'all',
  upc: 'all',
  dupe: 'all',
  ai: 'all',
  sort: 'none',
  order: [],
};

export type StagedFilterValue = string | string[];

export function stagedFilterValue(state: StagedFilterState, key: StagedFilterKey): StagedFilterValue {
  return state[key];
}

/** Prefix stored in the URL / selection for “does not have this issue”. */
export const STAGED_ISSUE_EXCLUDE_PREFIX = '-';

export function isStagedIssueExcludeToken(token: string): boolean {
  return token.startsWith(STAGED_ISSUE_EXCLUDE_PREFIX) && token.length > 1;
}

export function stagedIssueFilterBase(token: string): string {
  return isStagedIssueExcludeToken(token) ? token.slice(1) : token;
}

/**
 * Last token for each issue id wins, so `photo,-photo` cannot coexist.
 * Drops empty / malformed tokens (`-`, `--upc`).
 */
export function normalizeStagedIssueFilterValues(values: string[]): string[] {
  const byBase = new Map<string, string>();
  for (const raw of values) {
    if (!raw) continue;
    const exclude = isStagedIssueExcludeToken(raw);
    const base = stagedIssueFilterBase(raw);
    if (!base || base.startsWith(STAGED_ISSUE_EXCLUDE_PREFIX)) continue;
    byBase.set(base, exclude ? `${STAGED_ISSUE_EXCLUDE_PREFIX}${base}` : base);
  }
  return Array.from(byBase.values());
}

export function splitStagedIssueFilterValues(values: string[]): {
  include: string[];
  exclude: string[];
} {
  const include: string[] = [];
  const exclude: string[] = [];
  for (const token of normalizeStagedIssueFilterValues(values)) {
    if (isStagedIssueExcludeToken(token)) exclude.push(stagedIssueFilterBase(token));
    else include.push(token);
  }
  return { include, exclude };
}

export type StagedIssueFilterMode = 'off' | 'include' | 'exclude';

export function stagedIssueFilterMode(values: string[], id: string): StagedIssueFilterMode {
  const token = normalizeStagedIssueFilterValues(values).find(
    (t) => stagedIssueFilterBase(t) === id,
  );
  if (!token) return 'off';
  return isStagedIssueExcludeToken(token) ? 'exclude' : 'include';
}

/** off → include → exclude → off */
export function cycleStagedIssueFilterValue(values: string[], id: string): string[] {
  const base = stagedIssueFilterBase(id);
  if (!base) return normalizeStagedIssueFilterValues(values);
  const current = normalizeStagedIssueFilterValues(values);
  const without = current.filter((t) => stagedIssueFilterBase(t) !== base);
  const mode = stagedIssueFilterMode(current, base);
  if (mode === 'off') return [...without, base];
  if (mode === 'include') return [...without, `${STAGED_ISSUE_EXCLUDE_PREFIX}${base}`];
  return without;
}

/**
 * Include list is OR (has any of these issues). Exclude list is AND-NOT
 * (has none of these). Empty include means “don’t require an issue”.
 */
export function stagedRowMatchesIssues(gaps: Set<string>, selected: string[]): boolean {
  const { include, exclude } = splitStagedIssueFilterValues(selected);
  if (include.length > 0 && !include.some((id) => gaps.has(id))) return false;
  if (exclude.some((id) => gaps.has(id))) return false;
  return true;
}

/** True when the field is doing actual work (i.e. is worth serialising). */
export function isStagedFilterDirty(key: StagedFilterKey, value: StagedFilterValue): boolean {
  const field = FIELD_BY_KEY[key];
  if (field.multi) return Array.isArray(value) && value.length > 0;
  return value !== field.options[0].value;
}

export function hasActiveStagedFilters(state: StagedFilterState): boolean {
  if (state.q.trim()) return true;
  return STAGED_FILTER_KEYS.some((key) => isStagedFilterDirty(key, state[key]));
}

/** How many individual values are applied — the count shown next to the bar. */
export function stagedFilterValueCount(state: StagedFilterState): number {
  return STAGED_FILTER_KEYS.reduce((n, key) => {
    const value = state[key];
    if (Array.isArray(value)) return n + value.length;
    return n + (isStagedFilterDirty(key, value) ? 1 : 0);
  }, 0);
}

/**
 * Pill label for a field's value.
 * Multi with several values collapses to `first +n` so one wide selection
 * cannot push the rest of the bar off screen.
 */
export function stagedFilterValueLabel(
  field: StagedFilterFieldDef,
  value: StagedFilterValue,
  optionLabel?: (value: string) => string,
): string {
  const labelFor = (v: string) =>
    optionLabel ? optionLabel(v) : field.options.find((o) => o.value === v)?.label || v;
  if (!field.multi) return labelFor(String(value));
  const values = Array.isArray(value) ? value : [];
  if (values.length === 0) return field.allLabel || 'Any';
  if (field.allowExclude) {
    const { include, exclude } = splitStagedIssueFilterValues(values);
    const parts = [
      ...include.map((id) => labelFor(id)),
      ...exclude.map((id) => `not ${labelFor(id)}`),
    ];
    if (parts.length === 0) return field.allLabel || 'Any';
    if (parts.length === 1) return parts[0];
    return `${parts[0]} +${parts.length - 1}`;
  }
  if (values.length === 1) return labelFor(values[0]);
  return `${labelFor(values[0])} +${values.length - 1}`;
}

/**
 * Pills the bar should render: every field the user added, in add order, plus
 * anything applied out of band (a Top issues shortcut, a deep link) appended so
 * no active filter is invisible.
 */
export function stagedFilterPillKeys(state: StagedFilterState): StagedFilterKey[] {
  const seen = new Set<StagedFilterKey>();
  const keys: StagedFilterKey[] = [];
  for (const key of state.order) {
    if (isStagedFilterKey(key) && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  for (const key of STAGED_FILTER_KEYS) {
    if (!seen.has(key) && isStagedFilterDirty(key, state[key])) {
      seen.add(key);
      keys.push(key);
    }
  }
  return keys;
}

// Values are comma-joined in the URL, so a literal comma inside one (no brand
// has one today, but the list comes from the API) is escaped rather than lost.
const encodeValue = (v: string) => v.replace(/%/g, '%25').replace(/,/g, '%2C');
const decodeValue = (v: string) => v.replace(/%2C/gi, ',').replace(/%25/g, '%');

const joinValues = (values: string[]) => values.map(encodeValue).join(',');
const splitValues = (raw: string) =>
  raw
    .split(',')
    .map((v) => decodeValue(v.trim()))
    .filter(Boolean);

/**
 * Write the filter state onto search params, omitting anything at its default.
 * Params not owned by the filter bar (`id` deep links, `tab`) are preserved.
 */
export function stagedFiltersToParams(
  state: StagedFilterState,
  base?: URLSearchParams,
): URLSearchParams {
  const params = new URLSearchParams(base ? base.toString() : '');
  const set = (name: string, value: string) => {
    if (value) params.set(name, value);
    else params.delete(name);
  };

  set('q', state.q.trim());
  for (const key of STAGED_FILTER_KEYS) {
    const value = state[key];
    if (!isStagedFilterDirty(key, value)) {
      params.delete(key);
      continue;
    }
    set(key, Array.isArray(value) ? joinValues(value) : String(value));
  }

  const order = stagedFilterPillKeys(state);
  set('order', order.join(','));
  return params;
}

/** Read filter state back out of search params, ignoring anything unrecognised. */
export function stagedFiltersFromParams(params: URLSearchParams): StagedFilterState {
  const state: StagedFilterState = {
    ...EMPTY_STAGED_FILTER_STATE,
    brand: [],
    issues: [],
    order: [],
  };
  state.q = params.get('q') || '';

  for (const key of STAGED_FILTER_KEYS) {
    const raw = params.get(key);
    if (raw == null) continue;
    const field = FIELD_BY_KEY[key];
    if (field.multi) {
      const values = splitValues(raw);
      const allowed =
        field.options.length > 0
          ? values.filter((v) => {
              const base = field.allowExclude ? stagedIssueFilterBase(v) : v;
              return field.options.some((o) => o.value === base);
            })
          : values;
      (state[key] as string[]) = field.allowExclude
        ? normalizeStagedIssueFilterValues(allowed)
        : allowed;
    } else if (field.options.some((o) => o.value === raw)) {
      (state[key] as string) = raw;
    }
  }

  const order = (params.get('order') || '')
    .split(',')
    .map((k) => k.trim())
    .filter(isStagedFilterKey);
  state.order = stagedFilterPillKeys({ ...state, order });
  return state;
}

/** Serialise to a query string for saved views (filters only, no tab or deep link). */
export function stagedFiltersToQueryString(state: StagedFilterState): string {
  return stagedFiltersToParams(state).toString();
}

export function stagedFiltersFromQueryString(query: string): StagedFilterState {
  return stagedFiltersFromParams(new URLSearchParams(query));
}

/** Apply one value, keeping the pill order stable and adding the pill if new. */
export function setStagedFilterValue(
  state: StagedFilterState,
  key: StagedFilterKey,
  value: StagedFilterValue,
): StagedFilterState {
  const next: StagedFilterState = { ...state, [key]: value } as StagedFilterState;
  next.order = state.order.includes(key) ? state.order : [...state.order, key];
  return next;
}

/** Remove a pill: drops it from the bar and resets its value. */
export function removeStagedFilter(state: StagedFilterState, key: StagedFilterKey): StagedFilterState {
  return {
    ...state,
    [key]: stagedFilterDefault(key),
    order: state.order.filter((k) => k !== key),
  } as StagedFilterState;
}

/** Add a value to a multi field without disturbing what is already selected. */
export function addStagedFilterValue(
  state: StagedFilterState,
  key: StagedFilterKey,
  value: string,
): StagedFilterState {
  const field = FIELD_BY_KEY[key];
  if (!field.multi) return setStagedFilterValue(state, key, value);
  const current = (state[key] as string[]) || [];
  if (field.allowExclude) {
    const base = stagedIssueFilterBase(value);
    if (!base) return setStagedFilterValue(state, key, current);
    const wantExclude = isStagedIssueExcludeToken(value);
    const mode = stagedIssueFilterMode(current, base);
    if (!wantExclude && mode === 'include') return setStagedFilterValue(state, key, current);
    if (wantExclude && mode === 'exclude') return setStagedFilterValue(state, key, current);
    const without = current.filter((v) => stagedIssueFilterBase(v) !== base);
    const token = wantExclude ? `${STAGED_ISSUE_EXCLUDE_PREFIX}${base}` : base;
    return setStagedFilterValue(state, key, normalizeStagedIssueFilterValues([...without, token]));
  }
  if (current.includes(value)) return setStagedFilterValue(state, key, current);
  return setStagedFilterValue(state, key, [...current, value]);
}
